import { Injectable } from '@angular/core';

import { GoogleTagManagerService } from 'angular-google-tag-manager';
import * as Gtag from 'ga-gtag';

import { CartDetailsModel, CreatePurchaseRequestModel, GetPurchaseDetailsResponseModel } from '../models/cart.models';
import { GetWineTypeImagesResponseModel } from '../models/photo.models';
import {
	GetListingDetailsResponseModel,
	GetWineListingsLevelOneListItemModel,
	GetWineListingsLevelTwoPurchaseOptionModel,
	GetWineListingsLevelTwoResponseModel,
} from '../models/view-listings.models';
import { GetWineDetailsForLevelTwoViewModel } from '../models/winetype.models';

/**
 * Adds Google Tag Manager events to the dataLayer
 */
@Injectable({
	providedIn: 'root',
})
export class GtmEventsService {
	constructor(private gtmService: GoogleTagManagerService) {}

	// function gtag(){dataLayer.push(arguments)};

	/**
	 * Code for push data to gtm - This is a copy of what "gtag" does
	 */
	public gtag<Command extends keyof Gtag.GtagCommands>(command: Command, ...args: Gtag.GtagCommands[Command]) {
		try {
			console.log('GTAG: ', arguments);
			this.gtmService.getDataLayer().push(arguments);
		} catch {
			console.log('Error adding GTAG: ', arguments);
		}
	}

	/**
	 * Fires the login event
	 * @url https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#login
	 */
	public loginEvent(userId) {
		this.gtmService.getDataLayer().push({
			user_id: userId,
		});
		this.gtag('event', 'login', {});
	}

	/**
	 * Fires the logout event
	 */
	public logoutEvent() {
		this.gtmService.getDataLayer().push({
			user_id: undefined,
		});

		// No much event in google
		this.gtag('event', 'logout', {});
	}

	/**
	 * Transforms the listing details to the format that gtm expects
	 * @param listingDetails
	 * @param extra any extra data to be added
	 * @returns
	 */
	protected _transformListingItem(
		listingDetails: GetListingDetailsResponseModel | GetWineListingsLevelTwoPurchaseOptionModel,
		extra: Partial<Gtag.Item & Gtag.CustomParams> = {},
	): Gtag.Item & Gtag.CustomParams {
		return {
			item_id: `${listingDetails.wineTypeId}_${listingDetails.vintage}`,
			/*item_id: String(
        'listingId' in listingDetails
          ? listingDetails.listingId
          : listingDetails.id
      ),*/

			price: listingDetails.price,
			quantity: 'quantity' in listingDetails ? listingDetails.quantity : 1,

			item_brand: listingDetails.producerName,
			item_category: listingDetails.producerName,
			item_category2: listingDetails.wineTypeName,
			item_category3: listingDetails.varietyName,
			/*item_category4: listingDetails.isAccessory
        ? 'Accessory'
        : String(listingDetails.vintage),*/

			//bottle_quality_ullage: listingDetails.qualityUllage,
			//bottle_quality_capule: listingDetails.qualityCapsule,
			//bottle_quality_label: listingDetails.qualityLabel,
			item_variant: 'sizeName' in listingDetails ? listingDetails.sizeName : listingDetails.bottleSizeName,
			provenance: listingDetails.provenance,
			bottle_region: listingDetails.regionName ? listingDetails.regionName : 'N/A',
			...extra,
		};
	}

	protected _transformPurchaseDetails(purchases: GetPurchaseDetailsResponseModel): Gtag.Item & Gtag.CustomParams {
		return purchases.items
			.map((item, index) => {
				return item.listingIds.map<Gtag.Item>((listingId) => ({
					//item_id: String(listingId), // or is this item.id,
					item_id: `${item.wineTypeId}_${item.vintage}`,
					item_name: item.fullName,
					price: item.price, // https://developers.google.com/analytics/devguides/collection/ga4/apply-discount?client_type=gtm
					quantity: item.quantity,
					//discount: item.discountPrice ? item.price - item.discountPrice : 0,
					index: index,
					item_variant: item.bottleSize,

					//location_id: String(item.siteId)
				}));
			})
			.flat(1);
	}

	protected _transformCartDetails(cartDetails: CartDetailsModel): Gtag.Item & Gtag.CustomParams {
		return cartDetails.items.map<Gtag.Item>((item, index) => ({
			item_id: `${item.wineTypeId}_${item.vintage}`, // or is this item.id,
			item_name: item.fullName,
			price: item.discountPrice || item.price, // https://developers.google.com/analytics/devguides/collection/ga4/apply-discount?client_type=gtm
			quantity: item.quantity,
			discount: item.discountPrice ? item.price - item.discountPrice : 0,
			index: index,
			//location_id: String(item.siteId)
		}));
	}

	/**
	 * Sends a view_list event to google analytics to track a view of a listings page
	 * @param listingDetails GetWineListingsLevelTwoResponseModel
	 * @url https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#view_item_list
	 */
	public viewLevelTwoListingEvent(
		listingId: number,
		vintage: string,
		listingDetailsLvl2: GetWineListingsLevelTwoResponseModel,
		wineInfo: {
			images: GetWineTypeImagesResponseModel[];
			details: GetWineDetailsForLevelTwoViewModel;
		},
	) {
		this.gtag('event', 'view_item_list', {
			item_list_id: listingId,
			item_list_name: wineInfo.details.fullName,
			items: [
				...listingDetailsLvl2.featuredListings.map((listingDetails, index) =>
					this._transformListingItem(listingDetails, {
						index: index + 1, // Non zero base
						item_category4: listingDetails.isAccessory ? 'Accessory' : String(vintage),
					}),
				),
				...listingDetailsLvl2.paginatedResult.results.map<Gtag.Item>((listingDetails, index) =>
					this._transformListingItem(listingDetails, {
						index: index + listingDetailsLvl2.featuredListings.length + 1, // Start this list after the featured, and add 1 to remove zero base,
						item_category4: listingDetails.isAccessory ? 'Accessory' : String(vintage),
					}),
				),
			],
		});
	}

	/**
	 * Sends a view_event to google analytics to track a view of a single listing
	 * @param listingDetails GetListingDetailsResponseModel
	 * @url https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#view_item
	 */
	public viewListingItemEvent(listingDetails: GetListingDetailsResponseModel) {
		this.gtag('event', 'view_item', {
			currency: 'AUD',
			value: listingDetails.price,
			items: [
				this._transformListingItem(listingDetails, {
					index: 0,
					item_category4: listingDetails.isAccessory ? 'Accessory' : listingDetails.vintage,
				}),
			],
		});
	}

	/**
	 * Sends a view_event to google analytics to track a view of a single listing
	 * @param listingDetails GetListingDetailsResponseModel
	 * @url https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#view_item
	 */
	public addToWishlistEvent(
		listingDetails: GetListingDetailsResponseModel | GetWineListingsLevelTwoPurchaseOptionModel,
	) {
		let cat4 = undefined;

		if (listingDetails.isAccessory) {
			cat4 = 'Accessory';
		} else if ('vintage' in listingDetails) {
			cat4 = String(listingDetails.vintage);
		}

		this.gtag('event', 'add_to_wishlist', {
			currency: 'AUD',
			value: listingDetails.price,
			items: [
				this._transformListingItem(listingDetails, {
					index: 0,
					item_category4: cat4,
				}),
			],
		});
	}

	/**
	 * Sends a view_event to google analytics to track a view of a single listing
	 * @param listingDetails GetListingDetailsResponseModel
	 * @url https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#view_item
	 */
	public addToWishlistLevelOneEvent(listingDetails: GetWineListingsLevelOneListItemModel) {
		this.gtag('event', 'add_to_wishlist', {
			currency: 'AUD',
			value: listingDetails.lowestPrice,
			items: [
				{
					item_id: `${listingDetails.varietyId}_${listingDetails.vintage}`,

					price: listingDetails.lowestPrice,
					quantity: 'quantity' in listingDetails ? listingDetails.quantity : 1,

					item_brand: listingDetails.producerName,
					item_category: listingDetails.producerName,
					item_category2: listingDetails.wineTypeName,
					item_category3: listingDetails.varietyName,
					item_category4: String(listingDetails.vintage),

					provenance: listingDetails.provenance,
					bottle_region: listingDetails.regionName ? listingDetails.regionName : 'N/A',
				},
			],
		});
	}

	/**
	 * Fires the add to cart event
	 * @param quantity
	 * @param listingDetails
	 * @url https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#add_to_cart
	 */
	public addToCartEvent(
		quantity: number,
		listingDetails: GetListingDetailsResponseModel | GetWineListingsLevelTwoPurchaseOptionModel,
	) {
		const maybeVintage = listingDetails.fullName.match(/[0-9]{4}/);

		this.gtag('event', 'add_to_cart', {
			currency: 'AUD',
			value: listingDetails.price * quantity,
			items: [
				this._transformListingItem(listingDetails, {
					//index: 0,
					item_category4: listingDetails.isAccessory ? 'Accessory' : (maybeVintage?.[0] ?? 'N/A'),
					quantity,
				}),
			],
		});
	}

	/**
	 * Fires the view cart event
	 * @param cartDetails
	 * @url https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#view_cart
	 */
	public viewCartEvent(cartDetails: CartDetailsModel) {
		this.gtag('event', 'view_cart', {
			currency: 'AUD',
			value: cartDetails.totalAmount,
			items: this._transformCartDetails(cartDetails),
		});
	}

	/**
	 * Begin checkout event
	 * @param cartDetails
	 * @url https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#begin_checkout
	 */
	public beginCheckoutEvent(cartDetails: CartDetailsModel) {
		this.gtag('event', 'begin_checkout', {
			currency: 'AUD',
			value: cartDetails.totalAmount,
			items: this._transformCartDetails(cartDetails),
		});
	}

	/**
	 * Add shipping info
	 * @param cartDetails
	 * @url https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#add_shipping_info
	 */
	public addShippingInfoEvent(shippingInfo: CreatePurchaseRequestModel, cartDetails: CartDetailsModel) {
		this.gtag('event', 'add_shipping_info', {
			currency: 'AUD',
			value: cartDetails.totalAmount,
			items: this._transformCartDetails(cartDetails),
		});
	}

	/**
	 * Add payment info
	 * @param cartDetails
	 * @url https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#add_payment_info
	 */
	public addPaymentInfoEvent(cartDetails: CartDetailsModel) {
		this.gtag('event', 'add_payment_info', {
			currency: 'AUD',
			value: cartDetails.totalAmount,
			items: this._transformCartDetails(cartDetails),
		});
	}

	/**
	 * Raise purchase event
	 * @param cartDetails
	 * @url https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#purchase
	 */
	public purchasedEvent(cartDetails: GetPurchaseDetailsResponseModel, reference: string) {
		this.gtag('event', 'purchase', {
			currency: 'AUD',
			value: cartDetails.totalAmount,
			transaction_id: reference,
			items: this._transformPurchaseDetails(cartDetails),
		});
	}

	/**
	 * Triggers the search event
	 * @param searchTerm
	 * @url https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#search
	 */
	public searchEvent(searchTerm) {
		this.gtag('event', 'search', {
			search_term: searchTerm,
		});
	}
}
